import {useEffect, useRef, useState} from 'react';

interface UseTimedAttentionIdsResult {
  markIdsForAttention: (ids: Array<string>) => void;
  shouldDrawAttention: (id: string) => boolean;
}

/**
 * Tracks IDs that should receive temporary UI attention (for example: a newly created table row).
 *
 * Each ID passed to `markIdsForAttention` is marked immediately and automatically expires after
 * `durationMs`. Re-marking an ID resets its timer, and timers for different IDs run independently.
 *
 * @example
 * const {markIdsForAttention, shouldDrawAttention} = useTimedAttentionIds(10_000);
 * markIdsForAttention(['row_123']);
 * const rowClassName = shouldDrawAttention('row_123') ? 'myHighlightedRow' : '';
 */
export function useTimedAttentionIds(durationMs: number): UseTimedAttentionIdsResult {
  const [attentionIds, setAttentionIds] = useState<Array<string>>([]);
  const attentionTimeoutById = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const attentionTimeouts = attentionTimeoutById.current;
    return () => {
      attentionTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      attentionTimeouts.clear();
    };
  }, []);

  function markIdsForAttention(ids: Array<string>): void {
    if (ids.length === 0) {
      return;
    }

    setAttentionIds((previousAttentionIds) => {
      const updatedAttentionIds = new Set(previousAttentionIds);
      ids.forEach((id) => {
        updatedAttentionIds.add(id);
      });
      return Array.from(updatedAttentionIds);
    });

    ids.forEach((id) => {
      const existingTimeout = attentionTimeoutById.current.get(id);
      if (existingTimeout != null) {
        clearTimeout(existingTimeout);
      }
      const timeout = setTimeout(() => {
        setAttentionIds((previousAttentionIds) =>
          previousAttentionIds.filter((attentionId) => attentionId !== id)
        );
        attentionTimeoutById.current.delete(id);
      }, durationMs);
      attentionTimeoutById.current.set(id, timeout);
    });
  }

  function shouldDrawAttention(id: string): boolean {
    return attentionIds.includes(id);
  }

  return {
    markIdsForAttention,
    shouldDrawAttention,
  };
}
